import { Box, Card, Container, Grid } from "@mantine/core";
import DyslexiaMetrics, { DyslexiaMetricSummary } from "./sections/DyslexiaMetrics";
import { DyslexiaTestResult, TestLanguage } from "types/testTypes";
import { FixationSaccadeConfig, FixationSaccadeSeries } from "model/EyeMovement";
import { useEffect, useMemo, useState } from "react";

import DyslexiaEyeMovementCanvas from "./sections/DyslexiaEyeMovementCanvas";
import DyslexiaNormativeGraph from "./sections/DyslexiaNormativeGraph";
import DyslexiaOverview from "./sections/DyslexiaOverview";
import DyslexiaProbabilityCard from "./sections/DyslexiaProbabilityCard";
import { transformDyslexiaTestResult } from "@utils/TestUtils";

const DEFAULT_DYSLEXIA_BACKGROUND = "/img/image_dyslexia_text_image_en.png";

const FIXATION_DURATION_THRESHOLD_SECONDS = 0.7;

/*
 * This is only for demo purposes. The actual calculations should be done on the backend.
 */
const FIXATION_SERIES_CONFIG: FixationSaccadeConfig = {
  minFixationDuration: 0.1,
  minSamplesPerFixation: 2,
  dispersionThreshold: 5,
  samplingRateHz: 30,
  dropoutThresholdMultiplier: 1.75,
  headsetHFOV: 100,
  headsetVFOV: 100,
  mergeDistanceDegrees: 0.5,
};

const EMPTY_METRICS: DyslexiaMetricSummary = {
  totalFixations: 0,
  averageFixationDuration: 0,
  excessiveFixationsCount: 0,
  excessiveFixationsPercentage: 0,
  averageExcessiveFixationDuration: 0,
  normalFixationsCount: 0,
  normalFixationsPercentage: 0,
  averageNormalFixationDuration: 0,
};

const calculateAverage = (values: number[]): number => {
  if (!values.length) {
    return 0;
  }

  const total = values.reduce((sum, value) => sum + value, 0);
  return total / values.length;
};

export default function DyslexiaTestEntry({ entry }) {
  const results = useMemo(
    () => (transformDyslexiaTestResult(entry) as DyslexiaTestResult)?.results,
    [entry],
  );

  const [backgroundSource, setBackgroundSource] = useState<string>("");
  const [eyeMovements, setEyeMovements] = useState<FixationSaccadeSeries | null>(null);

  useEffect(() => {
    if (!results?.gazeDataWithDeltaList?.length) {
      setEyeMovements(null);
      setBackgroundSource(DEFAULT_DYSLEXIA_BACKGROUND);
      return;
    }

    const series = new FixationSaccadeSeries(results.gazeDataWithDeltaList, FIXATION_SERIES_CONFIG);
    setEyeMovements(series);

    const background = (() => {
      switch (results.testLanguage) {
        case TestLanguage.Turkish:
          return "/img/image_dyslexia_text_image_tr.png";
        case TestLanguage.Arabic:
          return "/img/image_dyslexia_text_image_ar.png";
        case TestLanguage.English:
        default:
          return DEFAULT_DYSLEXIA_BACKGROUND;
      }
    })();

    setBackgroundSource(background);
  }, [results]);

  const { fixationMetrics, percentile } = useMemo(() => {
    if (!eyeMovements?.fixations.length) {
      return { fixationMetrics: EMPTY_METRICS, percentile: 0 };
    }

    const durations = eyeMovements.fixations
      .map((fixation) => fixation.duration)
      .filter((duration) => duration > 0);

    if (!durations.length) {
      return { fixationMetrics: EMPTY_METRICS, percentile: 0 };
    }

    const totalFixations = durations.length;
    const excessiveFixations = durations.filter(
      (duration) => duration > FIXATION_DURATION_THRESHOLD_SECONDS,
    );
    const normalFixations = durations.filter(
      (duration) => duration <= FIXATION_DURATION_THRESHOLD_SECONDS,
    );

    const excessiveFixationsPercentage = (excessiveFixations.length / totalFixations) * 100;

    const calculatedPercentile = Math.min(95, Math.round(100 - excessiveFixationsPercentage));

    return {
      fixationMetrics: {
        totalFixations,
        averageFixationDuration: calculateAverage(durations),
        excessiveFixationsCount: excessiveFixations.length,
        excessiveFixationsPercentage: excessiveFixationsPercentage,
        averageExcessiveFixationDuration: calculateAverage(excessiveFixations),
        normalFixationsCount: normalFixations.length,
        normalFixationsPercentage: (normalFixations.length / totalFixations) * 100,
        averageNormalFixationDuration: calculateAverage(normalFixations),
      },
      percentile: calculatedPercentile,
    };
  }, [eyeMovements]);

  return (
    <Box>
      <Grid>
        <Grid.Col span={6}>
          <DyslexiaOverview percentile={percentile} />
        </Grid.Col>
        <Grid.Col span={6}>
          <DyslexiaNormativeGraph percentile={percentile} />
        </Grid.Col>
        <Grid.Col span={8}>
          <Card withBorder radius="md" shadow="md">
            <Container
              style={{
                height: "500px",
                width: "505px",
                position: "relative",
                overflow: "hidden",
                textAlign: "center",
                justifyContent: "end",
                zIndex: 100,
              }}
            >
              <DyslexiaEyeMovementCanvas
                series={eyeMovements || undefined}
                background={backgroundSource}
                excessiveThreshold={FIXATION_DURATION_THRESHOLD_SECONDS}
              />
            </Container>
          </Card>
        </Grid.Col>
        <Grid.Col span={4}>
          <DyslexiaMetrics metrics={fixationMetrics} columns={{ base: 1 }} />
        </Grid.Col>
        <Grid.Col span={12}>
          <DyslexiaProbabilityCard probability={Math.max(100 - percentile, 10)} />
        </Grid.Col>
      </Grid>
    </Box>
  );
}
